import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useSeo } from "@/lib/seo";
import { PawField } from "@/components/decor";
import { windowLabel } from "@/components/CheckoutPostcode";
import { trackCompletePayment } from "@/lib/pixel";

export default function PaymentSuccess() {
  useSeo("Order Confirmed", "Your Happy Bulldog goodies are on their way!");
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useStore();
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error
  const [orderNumber, setOrderNumber] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setOrderNumber(data.order_number);
        if (data.delivery_estimate) setEstimate(data.delivery_estimate);
        if (data.payment_status === "paid") {
          setStatus("paid");
          trackCompletePayment(data);
          if (!cleared.current) { clearCart(); cleared.current = true; }
          return;
        }
        if (data.payment_status === "expired") { setStatus("error"); return; }
      } catch { setStatus("error"); return; }
      attempts.current += 1;
      if (attempts.current > 8) { setStatus("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clearCart]);

  return (
    <div className="relative min-h-[70vh] flex items-center justify-center px-4 overflow-hidden">
      <PawField color="#7ED957" opacity={0.08} />
      <div className="relative bg-white rounded-[2rem] soft-shadow p-8 sm:p-12 max-w-lg w-full text-center">
        {status === "checking" && (<><Loader2 className="w-16 h-16 text-[#4CC9F0] mx-auto animate-spin" /><h1 className="font-display font-extrabold text-2xl text-[#19324A] mt-5">Confirming your order…</h1><p className="text-[#19324A]/60 mt-2">Just fetching the biscuits. One moment! 🐾</p></>)}
        {status === "paid" && (<><CheckCircle2 className="w-20 h-20 text-[#7ED957] mx-auto" /><h1 className="font-display font-extrabold text-3xl text-[#19324A] mt-5">Woohoo! 🎉</h1><p className="text-[#19324A]/75 mt-3 text-lg">Your Happy Bulldog goodies are officially on their way to joining the pack.</p>{orderNumber && <p className="mt-4 font-display font-bold text-[#19324A]">Order <span className="text-[#FF6B9D]">{orderNumber}</span></p>}{estimate && <p className="mt-2 text-sm text-[#19324A]/75" data-testid="success-arrival">📦 Estimated arrival <strong>{windowLabel(estimate)}</strong> to {estimate.postcode}{estimate.cutoff?.in_time && <span className="block text-xs font-semibold text-[#166534]">✓ In time for {estimate.cutoff.label}</span>}</p>}<div className="flex flex-col sm:flex-row gap-3 justify-center mt-7"><Link to="/shop" className="btn-primary">Keep Shopping</Link><Link to="/account" className="btn-outline">View My Orders</Link></div></>)}
        {status === "pending" && (<><Loader2 className="w-16 h-16 text-[#FFD93D] mx-auto" /><h1 className="font-display font-extrabold text-2xl text-[#19324A] mt-5">Payment processing</h1><p className="text-[#19324A]/60 mt-2">Your payment is still processing. We'll email your confirmation shortly.</p>{orderNumber && <p className="mt-3 font-display font-bold">Order {orderNumber}</p>}<Link to="/" className="btn-primary mt-6 inline-flex">Take Me Home</Link></>)}
        {status === "error" && (<><XCircle className="w-16 h-16 text-[#FF6B9D] mx-auto" /><h1 className="font-display font-extrabold text-2xl text-[#19324A] mt-5">Hmm, something went sideways</h1><p className="text-[#19324A]/60 mt-2">We couldn't confirm this order. If you were charged, please contact us and we'll sort it out.</p><Link to="/contact" className="btn-primary mt-6 inline-flex">Contact Us</Link></>)}
      </div>
    </div>
  );
}
